import {Link} from "react-router";
import {ArrowRight, Building2, KeyRound, User} from "lucide-react";
import {Badge} from "@nosible/shared/components/ui/badge";
import {Button} from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {useSession} from "@/account/lib/auth-client";
import {trpc} from "@/account/lib/trpc";
import {AccountPageHeader} from "@/account/components/AccountPageHeader";

export function ApiAccessPage() {
  const {data: session} = useSession();
  const access = trpc.user.getAccessibleApiKeys.useQuery(undefined, {
    enabled: Boolean(session),
  });

  return (
    <div className="account-ui-page account-settings">
      <AccountPageHeader
        title="API Access"
        description="Manage every API key available to your account and organizations."
        icon={KeyRound}
      />

      {access.isLoading ? (
        <p className="text-chromium-mist">Loading API access…</p>
      ) : !access.data?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>No active API access</CardTitle>
            <CardDescription>
              API keys are created inside an active subscription.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild>
              <Link to="/subscriptions">
                VIEW SUBSCRIPTIONS <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {access.data.map(({subscription, owner, keys}) => {
            const OwnerIcon = owner.type === "organization" ? Building2 : User;
            return (
              <Card key={subscription.id}>
                <CardHeader>
                  <KeyRound className="brand-card-icon" aria-hidden="true" />
                  <CardTitle>{subscription.name || subscription.plan.name}</CardTitle>
                  <CardDescription>
                    <span className="flex items-center gap-2">
                      <OwnerIcon className="h-4 w-4" aria-hidden="true" />
                      {owner.type === "organization"
                        ? `${owner.name} organization`
                        : "Personal subscription"}
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {keys.length === 0 ? (
                    <p className="text-sm text-chromium-mist">No active API keys.</p>
                  ) : (
                    <div className="space-y-2">
                      {keys.map((key) => (
                        <div
                          className="flex items-center justify-between border-b border-slate-core py-2"
                          key={key.id}
                        >
                          <span>{key.name}</span>
                          <Badge variant="outline">ACTIVE</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" className="w-full" asChild>
                    <Link to={`/subscriptions/${subscription.id}`}>
                      MANAGE KEYS <ArrowRight aria-hidden="true" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
